import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProtectedRoute({ children }) {
  const { user, loading, getToken } = useAuth();
  const location = useLocation();

  // localStorage is the fast path (no network round-trip on ordinary
  // navigation) and is what gets checked on every render below. It's only
  // ever ABSENT for a genuinely new device/browser/cleared-storage on an
  // account that may have already finished onboarding elsewhere -- that
  // case falls through to a one-time server check instead of assuming
  // "never onboarded" and re-running the whole wizard.
  const localOnboarded = !!localStorage.getItem('qude_onboarded');
  const needsServerCheck = !localOnboarded && location.pathname !== '/onboarding';

  const [serverChecked, setServerChecked] = useState(false);
  const [serverOnboarded, setServerOnboarded] = useState(false);

  useEffect(() => {
    if (loading || !user || !needsServerCheck || serverChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('no token');
        const res = await axios.get(`${API}/user/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const onboarded = !!res.data?.onboarded;
        if (onboarded) localStorage.setItem('qude_onboarded', '1'); // self-heal the cache
        setServerOnboarded(onboarded);
      } catch {
        if (!cancelled) setServerOnboarded(false); // fail safe -- same as today's default
      } finally {
        if (!cancelled) setServerChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, user, needsServerCheck, serverChecked, getToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (needsServerCheck) {
    if (!serverChecked) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (!serverOnboarded) {
      return <Navigate to="/onboarding" replace />;
    }
    // serverOnboarded is true -- localStorage was just healed above, fall through.
  }

  return children;
}
